/**
 * Product Gallery Enhanced
 *
 * Features:
 * - Autoscroll every 3 seconds with loop
 * - Pause on user interaction, resume after 5s
 * - Play videos (muted) when the visitor navigates to that slide
 * - Show poster/thumbnail while video loads
 */

class ProductGalleryEnhanced {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.pge-slides');
    this.slides = container.querySelectorAll('.pge-slide');
    this.thumbnails = container.querySelectorAll('.pge-thumbnail');
    this.dots = container.querySelectorAll('.pge-dot');
    this.prevBtn = container.querySelector('.pge-nav--prev');
    this.nextBtn = container.querySelector('.pge-nav--next');

    // Thumbnail navigation
    this.thumbnailsWrapper = container.querySelector('.pge-thumbnails-wrapper');
    this.thumbnailsSlider = container.querySelector('.pge-thumbnails');
    this.thumbPrevBtn = container.querySelector('.pge-thumb-nav--prev');
    this.thumbNextBtn = container.querySelector('.pge-thumb-nav--next');

    if (!this.slider || this.slides.length < 2) return;

    // Configuration
    this.autoscrollDelay = parseInt(container.dataset.autoscrollDelay) || 3000;
    this.resumeDelay = parseInt(container.dataset.resumeDelay) || 5000;

    // State
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoscrollInterval = null;
    this.resumeTimeout = null;
    this.isVideoPlaying = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.playFirstSlideIfVideo();
    this.startAutoscroll();
  }

  playFirstSlideIfVideo() {
    // Check if first slide is a video and autoplay it
    const firstSlide = this.slides[0];
    if (firstSlide) {
      const mediaType = firstSlide.dataset.mediaType;
      if (mediaType === 'video' || mediaType === 'external_video') {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.playMediaOnSlide(firstSlide);
        }, 100);
      }
    }
  }

  bindEvents() {
    // Navigation buttons
    this.prevBtn?.addEventListener('click', () => {
      this.pauseForInteraction();
      this.goToSlide(this.currentIndex - 1);
    });

    this.nextBtn?.addEventListener('click', () => {
      this.pauseForInteraction();
      this.goToSlide(this.currentIndex + 1);
    });

    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.querySelector('button')?.addEventListener('click', () => {
        this.pauseForInteraction();
        this.goToSlide(index);
      });
    });

    // Dot clicks (mobile)
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.pauseForInteraction();
        this.goToSlide(index);
      });
    });

    // Touch/swipe detection
    let touchStartX = 0;
    let touchEndX = 0;

    this.slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      this.pauseForInteraction();
    }, { passive: true });

    this.slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });

    // Mouse drag
    this.slider.addEventListener('mousedown', () => {
      this.pauseForInteraction();
    });

    // Video play buttons
    this.container.querySelectorAll('.pge-play-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const slide = e.target.closest('.pge-slide');
        if (slide) {
          this.playMediaOnSlide(slide);
        }
      });
    });

    // Thumbnail navigation arrows
    this.thumbPrevBtn?.addEventListener('click', () => {
      this.scrollThumbnails('prev');
    });

    this.thumbNextBtn?.addEventListener('click', () => {
      this.scrollThumbnails('next');
    });

    // Update thumbnail nav buttons on scroll
    this.thumbnailsSlider?.addEventListener('scroll', () => {
      this.updateThumbNavButtons();
    });

    // Initial thumbnail nav button state
    this.updateThumbNavButtons();
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        this.goToSlide(this.currentIndex - 1);
      }
    }
  }

  startAutoscroll() {
    this.stopAutoscroll();
    this.autoscrollInterval = setInterval(() => {
      this.autoAdvance();
    }, this.autoscrollDelay);
  }

  stopAutoscroll() {
    if (this.autoscrollInterval) {
      clearInterval(this.autoscrollInterval);
      this.autoscrollInterval = null;
    }
  }

  scheduleResume() {
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
    }
    this.resumeTimeout = setTimeout(() => {
      this.startAutoscroll();
    }, this.resumeDelay);
  }

  pauseForInteraction() {
    this.stopAutoscroll();
    this.scheduleResume();
  }

  autoAdvance() {
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.goToSlide(nextIndex, true);
  }

  goToSlide(index, isAutoScroll = false) {
    // Handle wrapping
    if (index < 0) {
      index = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      index = 0;
    }

    if (index === this.currentIndex) return;

    // Pause any playing video on current slide
    this.pauseCurrentMedia();

    // Update slides
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      const btn = thumb.querySelector('button');
      if (btn) {
        btn.setAttribute('aria-current', i === index ? 'true' : 'false');
      }
    });

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    // Scroll slide into view (horizontal scroll within the slider only)
    const targetSlide = this.slides[index];
    if (targetSlide) {
      this.slider.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    // Scroll thumbnail into view (use scrollIntoView with block: 'nearest' to avoid page scroll)
    const targetThumb = this.thumbnails[index];
    if (targetThumb && this.thumbnailsSlider) {
      // Calculate thumbnail position within the thumbnails container
      const thumbLeft = targetThumb.offsetLeft;
      const thumbWidth = targetThumb.offsetWidth;
      const containerWidth = this.thumbnailsSlider.clientWidth;
      const containerScrollLeft = this.thumbnailsSlider.scrollLeft;

      // Only scroll if thumbnail is not fully visible
      if (thumbLeft < containerScrollLeft) {
        this.thumbnailsSlider.scrollTo({
          left: thumbLeft,
          behavior: 'smooth'
        });
      } else if (thumbLeft + thumbWidth > containerScrollLeft + containerWidth) {
        this.thumbnailsSlider.scrollTo({
          left: thumbLeft + thumbWidth - containerWidth,
          behavior: 'smooth'
        });
      }
    }

    this.currentIndex = index;

    // Check for video on new slide
    setTimeout(() => {
      this.checkAndPlayVideo(targetSlide, isAutoScroll);
    }, isAutoScroll ? 500 : 300);
  }

  checkAndPlayVideo(slide, isAutoScroll = false) {
    const mediaType = slide?.dataset.mediaType;
    if (mediaType !== 'video' && mediaType !== 'external_video') return;

    // Do not start playback for an autoscroll pass. The gallery videos are
    // preload="none", so no video bytes are fetched until play() is called, and
    // the three on this PDP are ~1.7 MB together. Autoscrolling past a slide is
    // not a request to watch it, so leave the poster up and let the visitor
    // start it: the nav arrows, thumbnails, dots, swipe and the .pge-play-btn
    // all reach playMediaOnSlide with isAutoScroll false.
    //
    // Autoscroll deliberately keeps running over video slides now, instead of
    // being stopped by playMediaOnSlide, so the carousel still cycles.
    if (isAutoScroll) return;

    this.playMediaOnSlide(slide);
  }

  playMediaOnSlide(slide) {
    if (!slide) return;

    const mediaType = slide.dataset.mediaType;
    const poster = slide.querySelector('.pge-video-poster, .pge-model-poster');
    const videoContainer = slide.querySelector('.pge-video-container, .pge-model-container');

    if (!videoContainer) return;

    // Pause autoscroll while video plays
    this.stopAutoscroll();
    this.isVideoPlaying = true;

    if (mediaType === 'video') {
      this.playNativeVideo(slide, poster, videoContainer);
    } else if (mediaType === 'external_video') {
      this.playExternalVideo(slide, poster, videoContainer);
    } else if (mediaType === 'model') {
      this.show3DModel(slide, poster, videoContainer);
    }
  }

  playNativeVideo(slide, poster, videoContainer) {
    const video = videoContainer.querySelector('video');
    if (!video) return;

    // Keep poster visible initially
    poster.style.position = 'absolute';
    poster.style.top = '0';
    poster.style.left = '0';
    poster.style.width = '100%';
    poster.style.height = '100%';
    poster.style.zIndex = '2';

    // Show video container
    videoContainer.style.display = 'block';

    // Hide poster when video is ready to play.
    //
    // `canplay` and `playing` are edge-triggered, so a listener on its own is
    // not enough: an element that has ALREADY reached readyState >= 3 never
    // re-fires them, and the poster (z-index 2) would then stay stuck on top of
    // a video that is in fact playing underneath it. That is reachable today on
    // any second visit to a slide, because pauseCurrentMedia() only pauses and
    // does not reset readyState, and on the armAutoplayRetry() path, which can
    // call back in on an element that is already playing.
    //
    // This was ALSO a Chrome-only bug with a different cause, and that cause is
    // gone: layout/theme.liquid used to call play() on every <video> on the
    // page, and Chrome plays a muted video even inside a `display: none`
    // container, so both events fired long before this ran. That handler is now
    // scoped to `video[autoplay]:not([controls])` and these gallery videos carry
    // no `autoplay` attribute at all - the media_tag call in
    // snippets/product-gallery-enhanced-media.liquid passes `preload: 'none'`
    // and no autoplay - so that selector never matches them and nothing outside
    // this file starts them. Do not restore the old wording, and do not drop the
    // state check below: it is kept for the edge-triggered reason above, not for
    // the Chrome one.
    //
    // So treat readiness as state to be checked, not only an event to wait for.
    let posterHidden = false;
    const hidePoster = () => {
      if (posterHidden) return;
      posterHidden = true;
      poster.style.display = 'none';
      this.scheduleResume();
    };

    video.addEventListener('canplay', hidePoster, { once: true });
    video.addEventListener('playing', hidePoster, { once: true });

    // Configure and play video
    video.muted = true;
    video.loop = true;
    video.playsInline = true;

    const playPromise = video.play();

    if (playPromise) {
      playPromise
        .then(() => {
          // An already-playing video never re-fires the events above, so settle
          // the poster from the current state instead. If it is still buffering,
          // `canplay` has yet to fire and the listener above will handle it.
          if (!video.paused && video.readyState >= 3) hidePoster();
        })
        .catch((err) => {
          console.log('Video autoplay prevented:', err);
          // Show poster again if autoplay fails, then retry on the first user
          // gesture. iOS (e.g. Low Power Mode / Low Data Mode) rejects this
          // scripted play() but always allows a gesture-driven one, so without
          // the retry the play button stays forever on iPhones.
          poster.style.display = 'flex';
          videoContainer.style.display = 'none';
          this.scheduleResume();
          this.armAutoplayRetry(slide);
        });
    }

    // Handle video end (even with loop, for edge cases)
    video.addEventListener('ended', () => {
      if (!video.loop) {
        this.scheduleResume();
      }
    });
  }

  armAutoplayRetry(slide) {
    if (this.autoplayRetryArmed) return;
    this.autoplayRetryArmed = true;

    const retry = () => {
      document.removeEventListener('touchend', retry);
      document.removeEventListener('pointerup', retry);
      window.removeEventListener('scroll', retry);
      this.autoplayRetryArmed = false;
      if (slide.classList.contains('is-active')) {
        this.playMediaOnSlide(slide);
      }
    };

    // pointerup/touchend count as user activation, so the retried play() is
    // exempt from autoplay blocking; scroll does not, but a scroll retry is
    // harmless — a second rejection just re-arms via the catch above.
    document.addEventListener('touchend', retry, { passive: true });
    document.addEventListener('pointerup', retry, { passive: true });
    window.addEventListener('scroll', retry, { passive: true, once: true });
  }

  playExternalVideo(slide, poster, videoContainer) {
    const iframe = videoContainer.querySelector('iframe');
    if (!iframe) return;

    // Keep poster visible initially
    poster.style.position = 'absolute';
    poster.style.top = '0';
    poster.style.left = '0';
    poster.style.width = '100%';
    poster.style.height = '100%';
    poster.style.zIndex = '2';

    // Show video container
    videoContainer.style.display = 'block';

    // Hide poster after iframe loads
    iframe.addEventListener('load', () => {
      setTimeout(() => {
        poster.style.display = 'none';
        this.scheduleResume();
      }, 500);
    }, { once: true });

    // Force iframe to reload/start playing
    const src = iframe.src;
    if (src) {
      iframe.src = src;
    }
  }

  show3DModel(slide, poster, modelContainer) {
    // Show model, hide poster
    poster.style.display = 'none';
    modelContainer.style.display = 'block';
    this.scheduleResume();
  }

  pauseCurrentMedia() {
    // Pause all videos
    this.container.querySelectorAll('video').forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });

    // Pause YouTube videos
    this.container.querySelectorAll('.js-youtube').forEach(iframe => {
      iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });

    // Pause Vimeo videos
    this.container.querySelectorAll('.js-vimeo').forEach(iframe => {
      iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
    });

    this.isVideoPlaying = false;
  }

  scrollThumbnails(direction) {
    if (!this.thumbnailsSlider) return;

    const thumbWidth = this.thumbnails[0]?.offsetWidth || 80;
    const gap = 8;
    const scrollAmount = (thumbWidth + gap) * 2; // Scroll 2 thumbnails at a time

    if (direction === 'prev') {
      this.thumbnailsSlider.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    } else {
      this.thumbnailsSlider.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  updateThumbNavButtons() {
    if (!this.thumbnailsSlider || !this.thumbPrevBtn || !this.thumbNextBtn) return;

    const scrollLeft = this.thumbnailsSlider.scrollLeft;
    const scrollWidth = this.thumbnailsSlider.scrollWidth;
    const clientWidth = this.thumbnailsSlider.clientWidth;

    // Disable prev button if at start
    this.thumbPrevBtn.disabled = scrollLeft <= 0;

    // Disable next button if at end
    this.thumbNextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

// Initialize all galleries on page
function initProductGalleries() {
  document.querySelectorAll('.product-gallery-enhanced').forEach(gallery => {
    if (!gallery.dataset.initialized) {
      gallery.dataset.initialized = 'true';
      new ProductGalleryEnhanced(gallery);
    }
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductGalleries);
} else {
  initProductGalleries();
}

// Also re-init on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', initProductGalleries);
